import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from './AppIcon';
import Button from './ui/Button';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const isHomePage = location?.pathname === '/' || location?.pathname === '/homepage';

  const navigationItems = [
    { name: 'Home', path: '/homepage', icon: 'Home' },
    { name: 'About', path: '/about', icon: 'User' },
    { name: 'Portfolio', path: '/portfolio', icon: 'Briefcase' },
    { name: 'Web Projects', path: '/websites', icon: 'Globe' },
    { name: 'Skills', path: '/skills', icon: 'Code' },
    { name: 'Contact', path: '/contact', icon: 'Mail' }
  ];

  const moreItems = [];

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
      setIsScrolled(scrollTop > 20);
    };

    // Trigger check immediately on load
    setTimeout(handleScroll, 100);

    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isHomePage]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  const Logo = () => (
  <Link to="/" className="flex items-center space-x-3 group">
      <div className="relative">
        <div className="w-10 h-10 bg-gradient-brand rounded-lg flex items-center justify-center shadow-brand transition-brand group-hover:scale-105">
          <Icon name="Code2" size={20} color="white" strokeWidth={2.5} />
        </div>
        <div className="absolute -top-1 -right-1 w-3 h-3 bg-accent rounded-full animate-pulse-brand"></div>
      </div>
      <div className="flex flex-col">
        <span className="text-brand-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-blue-600 group-hover:text-primary transition-brand">
          Mustan-Dev
        </span>
        <span className="text-brand-xs font-medium text-muted-foreground -mt-1">
          Software Engineer & Developer
        </span>
      </div>
    </Link>
  );

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-header transition-all duration-300 ${
        isHomePage
          ? isScrolled
            ? 'bg-white/10 backdrop-blur-md border-b border-white/20'
            : 'bg-transparent backdrop-blur-none border-none'
          : isScrolled 
            ? 'bg-background/95 backdrop-blur-brand border-b border-border shadow-brand-sm' 
            : 'bg-background/80 backdrop-blur-brand'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Logo />

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navigationItems?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-brand-sm font-medium transition-brand hover:bg-muted ${
                  isActivePath(item?.path)
                    ? 'text-primary bg-primary/10 shadow-brand-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={item?.icon} size={16} />
                <span>{item?.name}</span>
              </Link>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center space-x-4">
            <Button
              variant="outline"
              size="sm"
              iconName="Download"
              iconPosition="left"
              className="hover-lift"
            >
              Resume
            </Button>
            <Button
              variant="default"
              size="sm"
              iconName="MessageCircle"
              iconPosition="left"
              className="bg-gradient-brand hover:shadow-brand"
              onClick={() => {
                window.open('https://tawk.to/chat/690327836ad0751954fdd0e0/1j8q54mrf', '_blank', 'width=400,height=600');
              }}
            >
              Let's Talk
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMenu}
            className="lg:hidden p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-brand"
            aria-label="Toggle menu"
          >
            <Icon name={isMenuOpen ? "X" : "Menu"} size={24} />
          </button>
        </div>
      </div>
      {/* Mobile Navigation */}
      <div 
        className={`lg:hidden transition-brand overflow-hidden ${
          isMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="bg-background/95 backdrop-blur-brand border-t border-border">
          <nav className="px-4 py-4 space-y-2">
            {[...navigationItems, ...moreItems]?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                onClick={closeMenu}
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-brand-base font-medium transition-brand ${
                  isActivePath(item?.path)
                    ? 'text-primary bg-primary/10 shadow-brand-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <Icon name={item?.icon} size={20} />
                <span>{item?.name}</span>
              </Link>
            ))}
          </nav>

          {/* Mobile CTA */}
          <div className="px-4 pb-4 pt-2 border-t border-border space-y-3">
            <Button
              variant="outline"
              fullWidth
              iconName="Download"
              iconPosition="left"
            >
              Download Resume
            </Button>
            <Button
              variant="default"
              fullWidth
              iconName="MessageCircle"
              iconPosition="left"
              className="bg-gradient-brand"
              onClick={() => {
                window.open('https://tawk.to/chat/690327836ad0751954fdd0e0/1j8q54mrf', '_blank', 'width=400,height=600');
              }}
            >
              Let's Talk
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;